import type { FileUploadProps, RadioValue } from "@wayflyer/flyui";
import {
  Button,
  ButtonGroup,
  ContentList,
  UploadArea,
  FilesList,
  Flex,
  FormLabel,
  FormLayout,
  Heading,
  Modal,
  RadioField,
  StatusBanner,
  Text,
} from "@wayflyer/flyui";
import { IconEditPencil16Line } from "@wayflyer/flyui-icons/16/line";
import { domAnimation, LazyMotion } from "framer-motion";
import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { formatPhoneNumberForDisplay } from "../..";
import type { IdvRequiredReason, KYCDocumentUploadType } from "../../api-types/associated_persons_schemas";
import type { PhoneNumber } from "../../api-types/jurisdictions/us_schemas";
import { transformDate } from "../../utils";
import type { Configuration, SupportedCountry } from "../interface";
import type { PersonIdvState } from "../verify-identity";

import { StyledCard, VerticalDivider } from "./styled";

type Role =
  | "Chief Executive Officer"
  | "Chief Financial Officer"
  | "Chief Operating Officer"
  | "Managing Member"
  | "General Partner"
  | "President"
  | "Vice President"
  | "Treasurer"
  | "No Executive Role";

type DisplayRoleBase = Exclude<Role, "No Executive Role">;

export type DisplayRole = DisplayRoleBase | `${DisplayRoleBase}, UBO` | "No significant role" | "UBO";

export interface PersonSummary {
  serverId: number;
  name: string;
  role: DisplayRole;
  isSignee?: boolean;
  powerOfAttorney?: string;
  isUbo?: boolean;
  isEligibleSignee?: boolean;
  requiresIdv?: boolean;
  idvSessionStatus?: PersonIdvState;
  loading?: boolean;
  idvRequired?: IdvRequiredReason;
  isMissingInformation: boolean;
  idvMailSentAt?: string;
  isMemberOfCompany: boolean;
  address: string;
  dob: string;
  countryOfBirth: string;
  cityOfBirth: string;
  ssn: string;
  email: string;
  nationality?: string;
  uboOwnershipPercentage?: string;
  gender?: string;
  taxResidency?: SupportedCountry;
  isSingleDirectorConfirmed?: boolean;
  phone_number?: PhoneNumber;
}

export interface PersonCardProps<T> extends PersonSummary {
  onStartVerification: (id: number) => void;
  onSigneeChange: (value: Record<string, RadioValue>) => void;
  onOpenPersonForm: (id?: number) => void;
  onRemovePerson: (id: number) => void;
  onReRequestIdentityPerson: (id: number) => void;
  kycConfiguration: Configuration;
  signeeCount: number;
  usePersonUploadManager: ({
    id,
    documentType,
  }: {
    id?: number;
    documentType?: KYCDocumentUploadType;
  }) => PersonUploadManagerReturn<T>;
}

/** Return type of upload manager hook; includes sizeLimitMB/supportedFileExtensions for building helperText */
export type PersonUploadManagerReturn<T> = FileUploadProps<T> & {
  sizeLimitMB?: number;
  supportedFileExtensions?: string[];
};

const useErrorLookup = (intl, status) => {
  if (!status?.last_error?.code) {
    return;
  }

  const lookup = {
    under_supported_age: intl.formatMessage({
      id: "Unable to verify user under 16",
      defaultMessage: "Unable to verify user under 16",
    }),
    consent_declined: intl.formatMessage({
      id: "Declined permission to verify",
      defaultMessage: "Declined permission to verify",
    }),
    document_expired: intl.formatMessage({
      id: "Identity document is expired",
      defaultMessage: "Identity document is expired",
    }),
    selfie_document_missing_photo: intl.formatMessage({
      id: "No photo in identity document",
      defaultMessage: "No photo in identity document",
    }),
  };

  return (
    lookup[status.last_error.code] ??
    intl.formatMessage({ id: "Verification failed", defaultMessage: "Verification failed" })
  );
};

const useGetVerifyBannerContent = (
  isReturning: boolean,
  idvMailSentAt: string | null,
  idvRequired: string,
  isMissingInformation: boolean
): { title: string; text: string } => {
  const intl = useIntl();

  const lookup: Partial<Record<IdvRequiredReason, string>> = {
    no_selfie: intl.formatMessage({
      id: "Previous identity verification is missing a selfie image.",
      defaultMessage: "Previous identity verification is missing a selfie image.",
    }),
    id_expired: intl.formatMessage({
      id: "ID document has expired.",
      defaultMessage: "ID document has expired.",
    }),
    idv_never_done: intl.formatMessage({
      id: "The previous identity verification was not passed.",
      defaultMessage: "The previous identity verification was not passed.",
    }),
    switch_idv_provider: intl.formatMessage({
      id: "The identity verification provider has changed.",
      defaultMessage: "The identity verification provider has changed.",
    }),
  };

  if (isMissingInformation) {
    return {
      title: intl.formatMessage({
        id: "Additional information required.",
        defaultMessage: "Additional information required.",
      }),
      text: intl.formatMessage({
        id: "This person requires their details to be updated because we are missing some information about them.",
        defaultMessage:
          "This person requires their details to be updated because we are missing some information about them.",
      }),
    };
  } else if (isReturning) {
    if (idvMailSentAt) {
      return {
        title: intl.formatMessage({
          id: "Identity verification required",
          defaultMessage: "Identity verification required",
        }),
        text: intl.formatMessage({
          id: "This person has been emailed and requested to perform identity verification again.",
          defaultMessage: "This person has been emailed and requested to perform identity verification again.",
        }),
      };
    }
    return {
      title: intl.formatMessage({
        id: "This person needs to complete identity verification again.",
        defaultMessage: "This person needs to complete identity verification again.",
      }),
      text: lookup[idvRequired],
    };
  } else {
    return {
      title: intl.formatMessage({
        id: "Identity verification required",
        defaultMessage: "Identity verification required",
      }),
      text: intl.formatMessage({
        id: "This person has been emailed and requested to perform identity verification.",
        defaultMessage: "This person has been emailed and requested to perform identity verification.",
      }),
    };
  }
};

const PersonInfoField = ({
  idvStatus,
  requiresIdv,
  handleStartVerification,
  handleReRequestIdentityPerson,
  idvRequired,
  idvMailSentAt,
  loading,
  isMissingInformation,
  handleOpenPersonForm,
}) => {
  const isReturning =
    idvRequired === "id_expired" ||
    idvRequired === "no_selfie" ||
    idvRequired === "idv_never_done" ||
    idvRequired === "switch_idv_provider";

  const intl = useIntl();
  const error = useErrorLookup(intl, idvStatus);
  const { title, text } = useGetVerifyBannerContent(isReturning, idvMailSentAt, idvRequired, isMissingInformation);

  if (isMissingInformation) {
    return (
      <StatusBanner
        title={title}
        variant="warning"
        data-testid="info-field-warning"
        subheading={
          <>
            <Text>{text}</Text>
            <ButtonGroup
              size="small"
              primaryAction={{
                content: intl.formatMessage({ id: "Update details", defaultMessage: "Update details" }),
                onAction: handleOpenPersonForm,
                loading,
              }}
              primaryActionVariant="Tertiary"
              primaryActionType="button"
            />
          </>
        }
      />
    );
  }

  if (!requiresIdv) {
    return null;
  }

  switch (idvStatus?.status) {
    case "verified":
      return (
        <StatusBanner
          variant="positive"
          data-testid="info-field-success"
          title={intl.formatMessage({ id: "Identity verified", defaultMessage: "Identity verified" })}
          subheading={intl.formatMessage({
            id: "This person has been verified.",
            defaultMessage: "This person has been verified.",
          })}
        />
      );
    case "processing":
      return (
        <StatusBanner
          variant="info"
          data-testid="info-field-processing"
          title={intl.formatMessage({
            id: "Identity verification in progress",
            defaultMessage: "Identity verification in progress",
          })}
          subheading={intl.formatMessage({
            id: "This person is currently verifying their identity.",
            defaultMessage: "This person is currently verifying their identity.",
          })}
        />
      );
    case "fraud":
      return (
        <StatusBanner
          variant="critical"
          data-testid="info-field-error"
          title={intl.formatMessage({
            id: "Identity verification failed",
            defaultMessage: "Identity verification failed",
          })}
          subheading={intl.formatMessage({
            id: "This person's verification was declined and they need to retry.",
            defaultMessage: "This person's verification was declined and they need to retry.",
          })}
        />
      );
    default:
      return error ? (
        <StatusBanner
          title={intl.formatMessage({
            id: "This person needs to complete identity verification again.",
            defaultMessage: "This person needs to complete identity verification again.",
          })}
          variant="critical"
          data-testid="info-field-error"
          subheading={
            <Flex direction="column" gap="2">
              <Text>{error}</Text>
              <ButtonGroup
                size="small"
                primaryAction={{
                  content: intl.formatMessage({ id: "Try again", defaultMessage: "Try again" }),
                  onAction: handleStartVerification,
                  loading,
                }}
                secondaryAction={
                  isReturning && !idvMailSentAt
                    ? {
                        content: intl.formatMessage({
                          id: "Send identity verification email",
                          defaultMessage: "Send identity verification email",
                        }),
                        onAction: handleReRequestIdentityPerson,
                        loading,
                      }
                    : undefined
                }
                primaryActionType="button"
              />
            </Flex>
          }
        />
      ) : (
        <StatusBanner
          title={title}
          variant="warning"
          data-testid="info-field-warning"
          subheading={
            <Flex direction="column" gap="2">
              <Text>{text}</Text>
              <ButtonGroup
                size="small"
                primaryAction={{
                  content: intl.formatMessage({ id: "Verify identity now", defaultMessage: "Verify identity now" }),
                  onAction: handleStartVerification,
                  loading,
                }}
                secondaryAction={
                  isReturning && !idvMailSentAt
                    ? {
                        content: intl.formatMessage({
                          id: "Send identity verification email",
                          defaultMessage: "Send identity verification email",
                        }),
                        onAction: handleReRequestIdentityPerson,
                        loading,
                      }
                    : undefined
                }
                primaryActionType="button"
              />
            </Flex>
          }
        />
      );
  }
};

export const PersonCard = <T,>({
  name,
  role,
  isSignee,
  serverId,
  idvSessionStatus,
  requiresIdv,
  loading,
  isEligibleSignee,
  signeeCount,
  powerOfAttorney,
  onSigneeChange,
  onOpenPersonForm,
  onRemovePerson,
  onReRequestIdentityPerson,
  onStartVerification,
  kycConfiguration: {
    jurisdictionSpecificConfiguration,
    associatedPersonUploadFile,
    jurisdictionSpecificFields,
    associatedPersonIdentification,
    associatedPersonPlaceOfBirth,
    personNationality,
    country,
  },
  usePersonUploadManager,
  idvRequired,
  isMissingInformation,
  idvMailSentAt,
  dob,
  countryOfBirth,
  cityOfBirth,
  address,
  ssn,
  email,
  nationality,
  isUbo,
  uboOwnershipPercentage,
  gender,
  taxResidency,
  phone_number,
}: PersonCardProps<T>) => {
  const intl = useIntl();
  const [removeModalOpen, setRemoveModalOpen] = useState(false);

  const handleStartVerification = useCallback(() => {
    onStartVerification(serverId);
  }, [onStartVerification, serverId]);

  const handleReRequestIdentityPerson = useCallback(() => {
    onReRequestIdentityPerson(serverId);
  }, [onReRequestIdentityPerson, serverId]);

  const handleOpenPersonForm = useCallback(() => {
    onOpenPersonForm(serverId);
  }, [onOpenPersonForm, serverId]);

  const handleOnRemovePerson = useCallback(() => {
    onRemovePerson(serverId);
  }, [onRemovePerson, serverId]);

  const fileUploadProps = usePersonUploadManager({ id: serverId });

  const isSigneeDisabled = useMemo(
    () => (jurisdictionSpecificConfiguration?.areMultipleSignersAllowed ? signeeCount === 1 && isSignee : isSignee),
    [isSignee, signeeCount, jurisdictionSpecificConfiguration]
  );

  const isPersonUploadFileAllowed = useMemo(
    (): boolean =>
      Boolean(
        associatedPersonUploadFile?.isOnlyForSigner
          ? associatedPersonUploadFile.isOnlyForSigner && isSignee
          : associatedPersonUploadFile
      ),
    [associatedPersonUploadFile, isSignee]
  );

  const displayFields: Array<{ [key: string]: string }> = [
    {
      name: intl.formatMessage({ id: "Date of birth", defaultMessage: "Date of birth" }),
      value: transformDate(dob),
    },
    {
      name: intl.formatMessage({ id: "Email address", defaultMessage: "Email address" }),
      value: email,
    },
    jurisdictionSpecificFields?.phoneNumber && {
      name: intl.formatMessage({
        id: "person-form: phone number",
        defaultMessage: "Phone number",
      }),
      value: formatPhoneNumberForDisplay(phone_number),
    },
    {
      name: intl.formatMessage({ id: "Residential address", defaultMessage: "Residential address" }),
      value: address,
    },
    associatedPersonIdentification && {
      name: associatedPersonIdentification.label(taxResidency || country),
      value: ssn || associatedPersonIdentification.optOutLabel,
    },
    jurisdictionSpecificFields?.taxResidency && {
      name: intl.formatMessage({ id: "Tax Residency", defaultMessage: "Tax Residency" }),
      value: taxResidency,
    },
    {
      name: intl.formatMessage({ id: "Role", defaultMessage: "Role" }),
      value: role,
    },
    {
      name: intl.formatMessage({ id: "UBO", defaultMessage: "UBO" }),
      value: isUbo ? "Yes" : "No",
    },
    jurisdictionSpecificFields?.uboOwnershipPercentage &&
      isUbo && {
        name: intl.formatMessage({ id: "UBO ownership percentage", defaultMessage: "UBO ownership percentage" }),
        value: uboOwnershipPercentage + "%",
      },
    associatedPersonPlaceOfBirth &&
      associatedPersonPlaceOfBirth.countryOfBirth?.required && {
        name: intl.formatMessage({ id: "Country of birth", defaultMessage: "Country of birth" }),
        value: countryOfBirth,
      },

    associatedPersonPlaceOfBirth &&
      associatedPersonPlaceOfBirth.cityOfBirth?.required && {
        name: intl.formatMessage({ id: "City of birth", defaultMessage: "City of birth" }),
        value: cityOfBirth,
      },
    personNationality && {
      name: intl.formatMessage({ id: "Nationality", defaultMessage: "Nationality" }),
      value: nationality,
    },

    jurisdictionSpecificFields?.gender && {
      name: intl.formatMessage({ id: "Gender", defaultMessage: "Gender" }),
      value: gender,
    },
  ];

  const hasFooterContent = isEligibleSignee || isPersonUploadFileAllowed;

  return (
    <>
      <StyledCard padding="none">
        <LazyMotion features={domAnimation}>
          <Flex direction="column" gap="4" padding={["5", "4", hasFooterContent ? "5" : "0", "4"]}>
            <Flex justify="space-between" align="center" wrap="wrap" gap="3">
              <Heading size="2xl">{name}</Heading>
              <Flex gap="2">
                <Button variant="Ghost" onClick={handleOpenPersonForm} iconLeft={IconEditPencil16Line}>
                  {intl.formatMessage({ id: "Update details", defaultMessage: "Update details" })}
                </Button>
                <VerticalDivider />
                <Button variant="CriticalGhost" onClick={() => setRemoveModalOpen(true)}>
                  {intl.formatMessage({ id: "Remove", defaultMessage: "Remove" })}
                </Button>
              </Flex>
            </Flex>

            <PersonInfoField
              idvStatus={idvSessionStatus}
              requiresIdv={requiresIdv}
              handleStartVerification={handleStartVerification}
              handleReRequestIdentityPerson={handleReRequestIdentityPerson}
              idvRequired={idvRequired}
              idvMailSentAt={idvMailSentAt}
              loading={loading}
              isMissingInformation={isMissingInformation}
              handleOpenPersonForm={handleOpenPersonForm}
            />

            <ContentList showFinalDivider={isEligibleSignee}>
              {(displayFields ?? []).filter(Boolean).map(({ name: label, value: content }) => (
                <ContentList.Item title={label} summary={content} key={label} />
              ))}
            </ContentList>

            {/* @todo should be removed after multiple contract signers are supported for all countries */}
            {isEligibleSignee && (
              <>
                <RadioField
                  label={intl.formatMessage({
                    id: "This person will be the contract signer",
                    defaultMessage: "This person will be the contract signer",
                  })}
                  defaultValue={isSignee ? true : false}
                  options={[
                    { label: intl.formatMessage({ id: "Yes", defaultMessage: "Yes" }), value: true },
                    { label: intl.formatMessage({ id: "No", defaultMessage: "No" }), value: false },
                  ]}
                  onChange={(value) => onSigneeChange({ isSignee: value })}
                  disabled={isSigneeDisabled}
                  name="contract-signer-radio"
                />
                {isSignee && jurisdictionSpecificFields?.powerOfAttorney ? (
                  <RadioField
                    label={intl.formatMessage({
                      id: "Type of Power of Attorney",
                      defaultMessage: "Type of Power of Attorney",
                    })}
                    helperText={intl.formatMessage({
                      id: `Power of attorney grants an individual or group of individuals authority to enter contracts on behalf of a business.
                          This can come in the form of sole authority or joint authority.
                          Where a contract signer has joint authority they do not have sole discretion to act on behalf of a business and additional joint signers should be added.`,
                      defaultMessage: `Power of attorney grants an individual or group of individuals authority to enter contracts on behalf of a business.
                        This can come in the form of sole authority or joint authority.
                        Where a contract signer has joint authority they do not have sole discretion to act on behalf of a business and additional joint signers should be added.`,
                    })}
                    name="power-of-attorney-type"
                    defaultValue={powerOfAttorney === "Sole" ? "Sole" : "Joint"}
                    options={[
                      { label: intl.formatMessage({ id: "Sole", defaultMessage: "Sole" }), value: "Sole" },
                      {
                        label: intl.formatMessage({ id: "Joint", defaultMessage: "Joint" }),
                        value: "Joint",
                      },
                    ]}
                    onChange={(value) => onSigneeChange({ powerOfAttorney: value })}
                  />
                ) : null}
              </>
            )}
            {isPersonUploadFileAllowed ? (
              <FormLayout>
                <FormLabel
                  htmlFor={`${ssn}-doc-upload`}
                  helperText={
                    associatedPersonUploadFile?.text
                      ? { text: associatedPersonUploadFile.text, asMarkdown: true }
                      : undefined
                  }
                >
                  {associatedPersonUploadFile?.title || ""}
                </FormLabel>
                <Flex direction="column" gap="1.5">
                  <UploadArea
                    label={fileUploadProps.label}
                    onFilesAdded={fileUploadProps.onFilesAdded}
                    sizeLimitMB={fileUploadProps.sizeLimitMB}
                    multiple
                  />
                  {fileUploadProps.uploadsState?.uploads?.length ? (
                    <FilesList
                      uploadsState={fileUploadProps.uploadsState}
                      onFileRemoved={fileUploadProps.onFileRemoved}
                    />
                  ) : null}
                </Flex>
              </FormLayout>
            ) : null}
          </Flex>
        </LazyMotion>
      </StyledCard>
      <Modal
        isOpen={removeModalOpen}
        onClose={() => setRemoveModalOpen(false)}
        title={intl.formatMessage({ id: "Remove individual", defaultMessage: "Remove individual" })}
        primaryAction={{
          content: intl.formatMessage({ id: "Remove", defaultMessage: "Remove" }),
          onAction: handleOnRemovePerson,
          critical: true,
        }}
        tertiaryAction={{
          content: intl.formatMessage({ id: "Cancel", defaultMessage: "Cancel" }),
          onAction: () => setRemoveModalOpen(false),
        }}
      >
        <Text>
          {intl.formatMessage(
            {
              id: "Are you sure you want to remove this individual",
              defaultMessage:
                "Are you sure you want to remove {name} as a beneficial owner or contract signer? This action cannot be undone.",
            },
            { name }
          )}
        </Text>
      </Modal>
    </>
  );
};
